
import React from 'react';
import { Star, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SessionRatingModalProps {
  isOpen: boolean;
  sessionRating: number;
  sessionComments: string;
  sessionRatingSubmitted: boolean;
  hasSubmittedRating: boolean;
  onClose: () => void;
  onRatingChange: (rating: number) => void;
  onCommentsChange: (comments: string) => void;
  onSubmit: () => void;
}

const SessionRatingModal: React.FC<SessionRatingModalProps> = ({
  isOpen,
  sessionRating,
  sessionComments,
  sessionRatingSubmitted,
  hasSubmittedRating,
  onClose,
  onRatingChange,
  onCommentsChange,
  onSubmit,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {hasSubmittedRating ? t('sessions.rating.modalTitleUpdate') : t('sessions.rating.modalTitle')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!sessionRatingSubmitted ? (
          <>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3">
                {hasSubmittedRating
                  ? t('sessions.rating.promptUpdate')
                  : t('sessions.rating.prompt')}
              </p>
              <div className="flex gap-2 justify-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => onRatingChange(star)}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-8 h-8 ${
                        star <= sessionRating
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('sessions.rating.commentsLabel')}
              </label>
              <textarea
                value={sessionComments}
                onChange={(e) => onCommentsChange(e.target.value)}
                placeholder={t('sessions.rating.commentsPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                rows={3}
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('sessions.rating.cancelButton')}
              </button>
              <button
                onClick={onSubmit}
                disabled={sessionRating === 0}
                className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {hasSubmittedRating ? t('sessions.rating.updateButton') : t('sessions.rating.submitButton')}
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="text-green-500 mb-2">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-gray-900 font-medium">
              {hasSubmittedRating ? t('sessions.rating.successMessageUpdate') : t('sessions.rating.successMessage')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionRatingModal;
